// Checkbox — native checkbox with a custom green box + check glyph.
// The real <input> drives state (accessible); the box is a visual sibling.

import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import Icon from './Icon';

export type CheckboxSize = 'sm' | 'md';

// Omit native `size` (a number for text inputs, meaningless here) so our
// variant prop owns the name.
export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  label?: ReactNode;
  size?: CheckboxSize;
}

const CHECK_SIZE: Record<CheckboxSize, number> = { sm: 12, md: 14 };

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, size = 'md', className = '', disabled, ...rest },
  ref,
) {
  const classes = ['app-checkbox', `app-checkbox--${size}`, disabled && 'is-disabled', className]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={classes}>
      <input
        ref={ref}
        type='checkbox'
        className='app-checkbox__input'
        disabled={disabled}
        {...rest}
      />
      <span className='app-checkbox__box' aria-hidden='true'>
        <Icon name='check' size={CHECK_SIZE[size]} className='app-checkbox__check' />
      </span>
      {label && <span className='app-checkbox__label'>{label}</span>}
    </label>
  );
});

export default Checkbox;
